import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';

import {
  billingPaymentReseted,
  getStripeKey,
  stripeClientSecretReset,
  stripeUpdatePaymentMethodError,
  updatePaymentMethod,
} from '../../../actions';
import { PAYMENT_METHOD_IDS, POLICY_STATUS } from '../../../constants';
import { PaymentMethod } from './PaymentMethod';
import { OneIncModal } from '../../common/OneIncModal';
import FigoLoadingOverlay from '../../common/FigoLoadingOverlay';

import bankIcon from '../../../assets/billingPayments/saving-bank.svg';
import checkIcon from '../../../assets/billingPayments/default-check.svg';
import creditCardIcon from '../../../assets/billingPayments/credit-card.svg';
import addIconBlue from '../../../assets/myPets/add-icon-blue.svg';
import loadingWheel from '../../../assets/lottieFiles/loading-blue.json';
import close from '../../../assets/close.svg';

import './css/PaymentMethodCard.css';
import { useStripeT177101, useWebViewReskin } from '../../../hooks';
import { StripeForm } from '../../common/stripe/StripeForm';
import FigoLottie from '../../common/FigoLottie';
import { useGetRecaptchaToken } from '../../../hooks/useRecaptchaValidator';
import { ModalContainer } from '../../common/ModalContainer';

const PaymentMethodCard = ({
  diamondPolicyId,
  policyInformation,
  policyNumber,
}) => {
  const { t } = useTranslation('myAccount');
  const i18nPayments = useTranslation('paymentMethod');
  const dispatch = useDispatch();
  const useMobileReskin = useWebViewReskin();
  const store = useSelector(({
    accountLogin,
    billingPayments,
    editPayment,
    oneInc,
    personalInformation,
    policies,
  }) => ({
    accountLogin,
    billingPayments,
    editPayment,
    oneInc,
    personalInformation,
    policies,
  }));
  const [showStripe, setShowStripe] = useState(false);
  const useStripe = useStripeT177101();
  const getRecaptchaToken = useGetRecaptchaToken();

  const {
    accountLogin: { userInfo },
    personalInformation,
    billingPayments: {
      billingDetails,
      loadingBillingInfo,
      stripeClientSecret,
      stripeError,
      stripeKey,
      stripeKeyLoading,
      updatingPaymentMethod,
    },
  } = store;

  const billingDetailsValidation =
    billingDetails?.ACHAccountNumber !== null
    || billingDetails?.CCCardNumber !== null;

  const userInformation = {
    address1: personalInformation.address1,
    address2: personalInformation.address2,
    city: personalInformation.city,
    state: personalInformation.state,
    zipCode: personalInformation.zipCode,
  };

  const closeStripeForm = () => setShowStripe(false);

  const onUpdatePaymentMethod = ({
    paymentMethodData,
    stripePaymentData,
  }) => {
    const { PolicyNumber } = policyInformation || {};

    dispatch(stripeClientSecretReset());
    setShowStripe(false);

    dispatch(updatePaymentMethod({
      paymentMethodData,
      policies: [],
      PolicyNumber,
      sameBillingAddress: true,
      stripePaymentData,
      userInformation,
    }));
  };

  const onErrorPaymentMethod = () => {
    // WIP: handle error
  };

  const onAddPaymentMethod = async () => {
    setShowStripe(true);

    const token = await getRecaptchaToken();

    if (token) {
      const policy = diamondPolicyId;
      dispatch(getStripeKey({ policy, token }));
    } else {
      setShowStripe(false);
    }
  };

  function renderUpdateButton() {
    if (stripeKeyLoading) {
      return (
        <div className="Payment-method-card-tooltip-button">
          <FigoLottie
            animationData={loadingWheel}
            height={30}
            width={30}
          />
        </div>
      );
    }

    if (showStripe) {
      return null;
    }

    return (
      <button
        className="Payment-method-card-tooltip-button"
        disabled={showStripe}
        onClick={onAddPaymentMethod}
        type="button"
      >
        <div className="No-click">
          <span className="Payment-method-card-button-details">
            {t('billingAndPayments.edit')}
          </span>
        </div>
      </button>
    );
  }

  const isCreditCard = billingDetails?.PayMethod
    === PAYMENT_METHOD_IDS.creditCard;
  const isBankAccount = billingDetails?.PayMethod === PAYMENT_METHOD_IDS.ach;
  const validPolicies =
    policyInformation?.Status === POLICY_STATUS.active
    || policyInformation?.Status === POLICY_STATUS.future;
  const stripeValidations = useStripe
    && !showStripe
    && !billingDetailsValidation
    && validPolicies;

  const onCloseStripeModalError =
    () => dispatch(stripeUpdatePaymentMethodError(''));

  useEffect(() => {
    dispatch(billingPaymentReseted());
  }, []);

  if (updatingPaymentMethod || loadingBillingInfo) {
    return (
      <FigoLoadingOverlay visible={updatingPaymentMethod} />
    );
  }

  return (
    <>
      {billingDetailsValidation && billingDetails && (
        <div
          className="Payment-method-card-container"
          data-default={useMobileReskin}
        >
          {useMobileReskin && (
            <div className="Payment-method-card-default-label">
              <img alt="" src={checkIcon} />

              {t('billingAndPayments.default')}
            </div>
          )}

          <PaymentMethod
            billingDetails={billingDetails}
            icon={isBankAccount ? bankIcon : creditCardIcon}
            isCreditCard={isCreditCard}
            title={isBankAccount ? t('billingAndPayments.bankAccountEnding')
              : t('billingAndPayments.creditCardEnding')}
          />

          <div>
            {useStripe ? (
              renderUpdateButton()
            ) : (
              <OneIncModal
                buttonClass="Payment-method-card-tooltip-button"
                onUpdatePaymentMethod={onUpdatePaymentMethod}
                selectedPolicy={policyInformation?.PolicyNumber
                  || policyNumber}
              >
                <span className="Payment-method-card-button-details">
                  {t('billingAndPayments.edit')}
                </span>
              </OneIncModal>
            )}
          </div>
        </div>
      )}

      {useStripe && showStripe && stripeClientSecret && stripeKey && (
        <StripeForm
          clientSecret={stripeClientSecret}
          email={userInfo?.LoginEmail || ''}
          name={`${userInfo?.FirstName || ''} ${userInfo?.LastName || ''}`}
          onClose={closeStripeForm}
          onConfirm={onUpdatePaymentMethod}
          onError={onErrorPaymentMethod}
          selectedPolicy={policyInformation?.PolicyNumber
            || policyNumber}
          stripeKey={stripeKey}
        />
      )}

      <ModalContainer
        onClose={onCloseStripeModalError}
        show={stripeError}
      >
        <div className="StripeModalMainContainer">
          <h1>{i18nPayments.t('stripe.errorTitle')}</h1>

          <button
            className="StripeModalClose"
            onClick={onCloseStripeModalError}
            type="button"
          >
            <img alt="" src={close} />
          </button>

          <span>{i18nPayments.t('stripe.errorMessage')}</span>

          <button
            className="StripeOkButton"
            onClick={onCloseStripeModalError}
            type="button"
          >
            {i18nPayments.t('stripe.ok')}
          </button>
        </div>
      </ModalContainer>

      {stripeValidations ? (
        <button
          className="Payment-method-button-add-method"
          onClick={onAddPaymentMethod}
          type="button"
        >
          <p>
            <img
              alt=""
              className="Billing-info-screen-button-image"
              src={addIconBlue}
            />
            {t('billingDetailsCard.addPaymentMethod')}
          </p>
        </button>
      ) : (
        <>
          {!useStripe && !billingDetailsValidation && validPolicies && (
            <OneIncModal
              buttonClass="Payment-method-button-add-method"
              onUpdatePaymentMethod={onUpdatePaymentMethod}
              selectedPolicy={policyInformation?.PolicyNumber || ''}
            >
              <p>
                <img
                  alt=""
                  className="Billing-info-screen-button-image"
                  src={addIconBlue}
                />
                {t('billingDetailsCard.addPaymentMethod')}
              </p>
            </OneIncModal>
          )}
        </>
      )}
    </>
  );
};

export default PaymentMethodCard;
